import React from 'react';
import { Link } from 'react-router-dom';
import { LegalLayout } from '../components/LegalLayout';

const isSolo = process.env.REACT_APP_SOLO_LANDING === 'true';
const linkCl = isSolo ? 'text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300' : 'text-indigo-600 hover:text-indigo-800';
const summaryBoxCl = isSolo ? 'bg-purple-50 dark:bg-purple-900/20 rounded-xl p-6 mb-8' : 'bg-indigo-50 dark:bg-indigo-900/20 rounded-xl p-6 mb-8';
const summaryHeadingCl = isSolo ? 'text-xl font-semibold text-purple-900 dark:text-purple-200 mt-0 mb-2' : 'text-xl font-semibold text-indigo-900 dark:text-indigo-200 mt-0 mb-2';
const summaryTextCl = isSolo ? 'text-purple-800 dark:text-purple-300 mb-0' : 'text-indigo-800 dark:text-indigo-300 mb-0';

const PrivacyPolicy = () => (
  <LegalLayout>
    <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">Privacy Policy</h1>
    <p className="text-lg text-gray-600 dark:text-gray-300 mb-8">
      Last updated: {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
    </p>

    <div className={summaryBoxCl}>
      <h2 className={summaryHeadingCl}>Summary</h2>
      <p className={summaryTextCl}>
        {isSolo ? 'SEN-Education' : 'SEN-Ed'} is a UK-based educational platform. We collect minimal data necessary to provide our services,
        store it securely on UK/EU servers, never sell your data, and comply with UK GDPR.
      </p>
    </div>

    <section className="mb-10">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">1. Who We Are</h2>
      <p className="text-gray-700 dark:text-gray-300">
        {isSolo ? 'SEN-Education' : 'SEN-Ed'} is an educational technology platform providing Special Educational Needs (SEN) curriculum resources,
        intervention planning tools, and SEND provision management for UK schools and teachers.
      </p>
      <ul className="text-gray-700 dark:text-gray-300">
        <li><strong>Platform:</strong> sen-education.co.uk</li>
        <li><strong>Contact:</strong> support@sen-ed.co.uk</li>
        <li><strong>Data Controller:</strong> {isSolo ? 'SEN-Education' : 'SEN-Ed'}</li>
        <li><strong>ICO Registration Number:</strong> ZC113316</li>
        <li>
          <strong>Data Protection Officer (DPO):</strong> Stephen Weaver, Weaver and Drew Consulting —{' '}
          <a href="mailto:info@weaverdrewconsulting.com" className={linkCl}>info@weaverdrewconsulting.com</a>
        </li>
      </ul>
    </section>

    <section className="mb-10">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">2. What Data We Collect</h2>
      <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-200">Account Information</h3>
      <ul className="text-gray-700 dark:text-gray-300">
        <li>Email address (for authentication and communication)</li>
        <li>Name (optional, for personalisation)</li>
        <li>School name and role (for school accounts)</li>
      </ul>
      <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-200">Usage Data</h3>
      <ul className="text-gray-700 dark:text-gray-300">
        <li>Resources accessed and searches performed</li>
        <li>Feature usage patterns (to improve the platform)</li>
        <li>Session information (login times, device type)</li>
      </ul>
      <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-200">Student Data (Schools Only)</h3>
      <p className="text-gray-700 dark:text-gray-300">
        For school accounts, we may process student information entered by school staff for the purpose of
        curriculum planning and intervention tracking. This is processed under the school&apos;s data controller
        responsibilities, with {isSolo ? 'SEN-Education' : 'SEN-Ed'} acting as a data processor.
      </p>
    </section>

    <section className="mb-10">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">3. How We Use Your Data</h2>
      <p className="text-gray-700 dark:text-gray-300">We use your data to:</p>
      <ul className="text-gray-700 dark:text-gray-300">
        <li>Provide and maintain our educational services</li>
        <li>Authenticate your account and manage access</li>
        <li>Send important service updates and communications</li>
        <li>Improve our platform based on usage patterns</li>
        <li>Ensure security and prevent fraud</li>
        <li>Comply with legal obligations</li>
      </ul>
      <p className="text-gray-700 dark:text-gray-300">
        <strong>We never sell your data to third parties.</strong>
      </p>
    </section>

    <section className="mb-10">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">4. Legal Basis for Processing</h2>
      <p className="text-gray-700 dark:text-gray-300">Under UK GDPR, we process your data based on:</p>
      <ul className="text-gray-700 dark:text-gray-300">
        <li><strong>Contract:</strong> To provide services you&apos;ve signed up for</li>
        <li><strong>Legitimate interests:</strong> To improve our platform and maintain security</li>
        <li><strong>Consent:</strong> For optional marketing communications and analytics cookies</li>
        <li><strong>Legal obligation:</strong> To comply with applicable laws</li>
      </ul>
    </section>

    <section className="mb-10">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">5. Data Storage &amp; Security</h2>
      <ul className="text-gray-700 dark:text-gray-300">
        <li><strong>Location:</strong> Data is stored on Google Cloud (Firebase) servers in the UK/EU region (europe-west3)</li>
        <li><strong>Encryption:</strong> All data is encrypted in transit (TLS) and at rest</li>
        <li><strong>Access:</strong> Role-based access controls limit data access to authorised personnel; school data is isolated per-tenant by <code>schoolId</code></li>
        <li><strong>Backups:</strong> Firestore Point-in-Time Recovery is enabled and Google Cloud provides platform-level redundancy. Customer-initiated exports are available on request.</li>
      </ul>
      <p className="text-gray-700 dark:text-gray-300">
        See our <Link to="/security" className={linkCl}>Security page</Link> for more details.
      </p>
    </section>

    <section className="mb-10">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">6. Data Retention</h2>
      <ul className="text-gray-700 dark:text-gray-300">
        <li><strong>Account data:</strong> Retained while your account is active, deleted within 30 days of account closure</li>
        <li><strong>Usage analytics:</strong> Aggregated data retained for up to 2 years</li>
        <li><strong>Student data:</strong> Retained according to school&apos;s data retention policy; schools can request deletion at any time</li>
      </ul>
    </section>

    <section className="mb-10">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">7. Your Rights (UK GDPR)</h2>
      <p className="text-gray-700 dark:text-gray-300">You have the right to:</p>
      <ul className="text-gray-700 dark:text-gray-300">
        <li><strong>Access:</strong> Request a copy of your personal data</li>
        <li><strong>Rectification:</strong> Correct inaccurate data</li>
        <li><strong>Erasure:</strong> Request deletion of your data (&quot;right to be forgotten&quot;)</li>
        <li><strong>Portability:</strong> Receive your data in a portable format</li>
        <li><strong>Restriction:</strong> Limit how we process your data</li>
        <li><strong>Objection:</strong> Object to certain processing activities</li>
        <li><strong>Withdraw consent:</strong> Where processing is based on consent</li>
      </ul>
      <p className="text-gray-700 dark:text-gray-300">
        To exercise these rights, contact us at <a href="mailto:support@sen-ed.co.uk" className={linkCl}>support@sen-ed.co.uk</a>.
        We will respond within 30 days.
      </p>
    </section>

    <section className="mb-10">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">8. Cookies</h2>
      <p className="text-gray-700 dark:text-gray-300">
        We use cookies to provide essential functionality and, with your consent, to understand how you use our platform.
      </p>
      <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-200">Essential Cookies</h3>
      <p className="text-gray-700 dark:text-gray-300">Required for the platform to function:</p>
      <ul className="text-gray-700 dark:text-gray-300">
        <li><strong>Authentication:</strong> Keep you logged in securely</li>
        <li><strong>Security:</strong> Prevent cross-site request forgery</li>
        <li><strong>Preferences:</strong> Remember your cookie consent choice</li>
      </ul>
      <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-200">Analytics Cookies (Optional)</h3>
      <p className="text-gray-700 dark:text-gray-300">
        With your consent, we use analytics to understand usage patterns and improve our platform.
        You can accept or reject these in the cookie consent banner.
      </p>
      <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-200">Managing Cookies</h3>
      <p className="text-gray-700 dark:text-gray-300">
        You can manage your cookie preferences through our <Link to="/cookies" className={linkCl}>Cookie policy</Link>, the cookie consent banner (first visit), and your browser settings.
      </p>
    </section>

    <section className="mb-10">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">9. Third-Party Services &amp; AI Processing</h2>
      <p className="text-gray-700 dark:text-gray-300">We use the following third-party services:</p>
      <ul className="text-gray-700 dark:text-gray-300">
        <li><strong>Google Firebase:</strong> Authentication, database, hosting, and storage (UK/EU servers, europe-west3)</li>
        <li><strong>Stripe:</strong> Payment processing for solo subscriptions (PCI DSS compliant)</li>
        <li><strong>SendGrid:</strong> Transactional email for subscription lifecycle events</li>
        <li><strong>Sentry:</strong> Frontend error and performance telemetry (no student data)</li>
        <li><strong>OpenAI:</strong> Large-language-model processing for EHCNA drafting, EHCP Section E/F extraction, therapist report summarisation, termly target suggestions, intervention grouping, and the Sensi assistant</li>
      </ul>

      <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-200">How we use OpenAI</h3>
      <p className="text-gray-700 dark:text-gray-300">
        We use OpenAI to assist SENCOs and teachers with drafting and extraction tasks that
        would otherwise be done manually. Before any text is sent to OpenAI we apply a
        server-side redaction layer that removes direct identifiers — student names,
        parent and carer names, email addresses, UK phone numbers, NHS numbers, dates,
        UK postcodes, and street addresses — and substitutes the student&apos;s name with a
        pseudonym such as &quot;the student&quot;. For EHCP and therapist documents we further
        narrow the payload to the relevant section window rather than sending the entire
        document.
      </p>
      <p className="text-gray-700 dark:text-gray-300">
        After redaction, the content sent to OpenAI is no longer directly identifying but
        still describes the student&apos;s SEND profile (needs, diagnoses, support history)
        — this is special category data. Our agreement with OpenAI under their API terms
        means inputs and outputs are not used to train their models. A record of which
        redactions were applied is kept in our internal audit log for each AI call.
      </p>
      <p className="text-gray-700 dark:text-gray-300">
        If you do not wish your data to be processed by OpenAI, SENCO users can disable
        AI features for their school in <Link to="/settings" className={linkCl}>School settings</Link>;
        the platform&apos;s core record-keeping continues to work without them.
      </p>

      <p className="text-gray-700 dark:text-gray-300">
        Each service has its own privacy policy and complies with applicable data
        protection laws. A full list of subprocessors (including data categories, region,
        and contractual safeguards) is available on request from our
        Data Protection Officer.
      </p>
    </section>

    <section className="mb-10">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">10. Affiliate Links</h2>
      <p className="text-gray-700 dark:text-gray-300">
        Some resource links on this platform are affiliate links. When you click on these and make a purchase,
        we may receive a small commission at no additional cost to you. We only recommend resources we
        genuinely find useful for educators.
      </p>
    </section>

    <section className="mb-10">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">11. Children&apos;s Privacy</h2>
      <p className="text-gray-700 dark:text-gray-300">
        {isSolo ? 'SEN-Education' : 'SEN-Ed'} is designed for use by educators and school staff, not directly by children.
        Student data entered by schools is processed under the school&apos;s responsibilities as data controller.
      </p>
    </section>

    <section className="mb-10">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">12. Changes to This Policy</h2>
      <p className="text-gray-700 dark:text-gray-300">
        We may update this privacy policy from time to time. We will notify you of significant changes
        via email or a notice on our platform. The &quot;last updated&quot; date at the top indicates when the
        policy was last revised.
      </p>
    </section>

    <section className="mb-10">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">13. Contact &amp; Complaints</h2>
      <p className="text-gray-700 dark:text-gray-300">
        For privacy-related queries or to exercise your rights:
      </p>
      <ul className="text-gray-700 dark:text-gray-300">
        <li><strong>Email:</strong> <a href="mailto:support@sen-ed.co.uk" className={linkCl}>support@sen-ed.co.uk</a></li>
        <li>
          <strong>Data Protection Officer:</strong> Stephen Weaver, Weaver and Drew Consulting —{' '}
          <a href="mailto:info@weaverdrewconsulting.com" className={linkCl}>info@weaverdrewconsulting.com</a>
        </li>
      </ul>
      <p className="text-gray-700 dark:text-gray-300">
        If you&apos;re not satisfied with our response, you have the right to lodge a complaint with the{' '}
        <a href="https://ico.org.uk/make-a-complaint/" target="_blank" rel="noopener noreferrer" className={linkCl}>Information Commissioner&apos;s Office (ICO)</a>{' '}
        (ICO registration number: ZC113316).
      </p>
    </section>
  </LegalLayout>
);

export default PrivacyPolicy;
